/* eslint-disable max-len */
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components/macro';
import { Frame } from 'components/Frame/Frame';
import { MobileSide } from 'components/MobileSide/MobileSide';
import { useViewport } from 'hooks/useViewport';
import { theme } from 'theme';
import { Sidebar, SidebarActions } from 'components/Sidebar/Sidebar';
import { useTypedSelector } from 'redux/reducers';
import { useToggle } from 'hooks/useToggle';
import { Modal } from 'components/Modal/Modal';
import { Heading } from 'components/Heading/Heading';
import { Paragraph } from 'components/Paragraph/Paragraph';
import { Button } from 'components/Button/Button';
import { useDispatch } from 'react-redux';
import { showInformation } from 'redux/actions/informationAction';
import marked from 'marked';
import { GlobalStyle } from 'globalStyles';
import { ContainerPortrait } from './ContainerPortrait';
import { NavigationMobile } from 'components/Navigation/NavigationMobile/NavigationMobile';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { TextLink } from 'components/TextLink/TextLink';

const StyledContainer = styled.main`
  height: 100%;
  display: flex;
  padding: ${({ theme }) => theme.spacing[1]};
  padding-left: 0;

  @media (min-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[4]} ${theme.spacing[8]}`};
  }
`;

const SidebarFooter = styled.footer`
  display: flex;

  > * + * {
    margin-left: ${({ theme }) => theme.spacing[3]};
  }
`;

const SidebarFooterText = styled.div`
  flex: 1;
`;

export const Container: React.FC = () => {
  const { width, orientation } = useViewport();
  const [showMobileNavigation, toggleMobileNavigation] = useToggle();
  const { selected: activeTopic, contentVisible } = useTypedSelector((state) => state.topic);
  const informationVisible = useTypedSelector((state) => state.indormation.visible);
  const dispatch = useDispatch();

  const closeIntroduction = useCallback(() => dispatch(showInformation(false)), [dispatch]);

  const showSidebarToggle = useMemo(() => {
    return !!activeTopic.sidebarContent;
  }, [activeTopic.sidebarContent]);

  const sidebarActions = useMemo((): SidebarActions => {
    if (activeTopic.externalLink) {
      return [{ id: 1, icon: faExternalLinkAlt, tag: 'a', href: activeTopic.externalLink }];
    }
    return [];
  }, [activeTopic.externalLink]);

  const SidebarFooterContent = useCallback(() => {
    if (!activeTopic.externalLink) {
      return null;
    }

    return (
      <SidebarFooter>
        <SidebarFooterText>
          <Heading as="h4">Meer weten?</Heading>
          <Paragraph weight="light">Op onze website vind je nog meer informatie over dit onderwerp.</Paragraph>
        </SidebarFooterText>
        <Button tag="a" href={activeTopic.externalLink} label="Lees verder" />
      </SidebarFooter>
    );
  }, [activeTopic.externalLink]);

  if (orientation === 'portrait') {
    return (
      <React.Fragment>
        <GlobalStyle />
        <ContainerPortrait />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <GlobalStyle />
      <StyledContainer>
        {width < theme.breakpoints.lg && (
          <React.Fragment>
            {showMobileNavigation && <NavigationMobile onMenuClick={toggleMobileNavigation} />}
            <MobileSide onMenuClick={toggleMobileNavigation} />
          </React.Fragment>
        )}
        {activeTopic.id !== 0 && (
          <Sidebar
            show={contentVisible}
            actions={sidebarActions}
            showToggle={showSidebarToggle}
            position={activeTopic.sidebarPosition}
            footer={activeTopic.externalLink && <SidebarFooterContent />}
          >
            <div dangerouslySetInnerHTML={{ __html: activeTopic.sidebarContent ? marked(activeTopic.sidebarContent) : 'r' }} />
          </Sidebar>
        )}
        <Frame />
        <Modal show={informationVisible}>
          <Heading size="h1" as="h1">Architectuur CZO</Heading>
          <Paragraph>In de eerste fase van CZO Flex Level zijn de architectuur, visie en bijna 200 EPA’s gemaakt die nodig zijn voor het nieuwe CZO-opleidingsstelsel voor (vervolg-) opleidingen voor gespecialiseerd verpleegkundigen, operatieassistenten en anesthesiemedewerkers. Die opleidingen zijn straks flexibel en vernieuwend. Zorgverleners krijgen daardoor meer carrièremogelijkheden: ze kunnen makkelijker in- en doorstromen én ze zijn klaar voor de zorgvraag van morgen. </Paragraph>
          <Paragraph>Nu zijn de praktijk- en theorieopleiders en professionals aan zet. Zij ontwerpen samen nieuwe leerroutes: dat kunnen vernieuwde CZO-opleidingen zijn maar ook individuele leerroutes. De visie geeft richting aan het ‘HOE’: aan het vernieuwen van de manier van opleiden. Deze architectuurplaat laat zien ‘WAT’ er nodig is: voor het flexibiliseren, binnen welke kaders dat moet en welke partijen (actoren) zijn betrokken. De knoppen onderaan de plaat verwijzen per architectuurelement naar meer informatie en vervolgens naar informatie op de website van CZO Flex Level. Op de plaat klikken per element kan ook. </Paragraph>
          <Paragraph>We hopen praktijk- en theorieopleiders, en alle andere bij (vervolg)opleidingen betrokken professionals, met deze plaat en de informatie op de site samen op weg te helpen. Om de werking van producten en architectuur van CZO Flex Level in de praktijk te kunnen toetsen én verbeteren, is samenwerking tussen CZO Flex Level en opleidingsinstellingen en zorginstellingen en -aanbieders in fase B een voorwaarde.</Paragraph>
          <Paragraph weight="semibold">
            Heb je na deze architectuurplaat vragen? <br />Neem contact op via&nbsp;
            <TextLink 
              label="czoflexlevel@nvz-ziekenhuizen.nl" 
              href="mailto:czoflexlevel@nvz-ziekenhuizen.nl?subject=Vragen%20architectuurplaat" 
              tag="a" 
              target="_blank" 
            />
          </Paragraph>
          <Button tag="button" onClick={closeIntroduction} label="Bekijk interactive" />
        </Modal>
      </StyledContainer>
    </React.Fragment>
  );
};